import store from "@/store";
import { ContractStatusCodes } from "@/models/contracts";
import { Location, Route } from "vue-router";
import { RegexMatch } from "@/data/enums/router";
import { $bus } from "@/boot/event-bus";

export default [
  {
    path: "products",
    component: () =>
      import("@/views/admin/clients/client/contractProducts/index.vue"),
    children: [
      {
        path: "",
        name: "adminClientCProds",
        redirect: "subscriptions"
      },
      {
        path: "subscriptions",
        name: "adminClientSubscriptions",
        redirect: { name: "adminClientActiveSubscriptions" },
        component: () =>
          import(
            "@/views/admin/clients/client/contractProducts/subscriptions/index.vue"
          ),
        props: (route: Route) => ({
          clientId: route.params.clientId,
          routes: require("./menu").cProdSubscriptionsTabigation
        }),
        meta: {
          backTo: "_.subscriptions",
          title: "_.subscriptions"
        },
        children: [
          {
            path: "active",
            name: "adminClientActiveSubscriptions",
            component: () =>
              import(
                "@/components/app/global/contractProducts/productsTable.vue"
              ),
            props: {
              statuses: [
                ContractStatusCodes.ACTIVE,
                ContractStatusCodes.AWAITING_ACTIVATION,
                ContractStatusCodes.PENDING,
                ContractStatusCodes.SUSPENDED
              ]
            }
          },
          {
            path: "cancelled",
            name: "adminClientCancelledSubscriptions",
            component: () =>
              import(
                "@/components/app/global/contractProducts/productsTable.vue"
              ),
            props: {
              statuses: [
                ContractStatusCodes.CANCELLED,
                ContractStatusCodes.CLOSED
              ]
            }
          },
          {
            path: "all",
            name: "adminClientAllSubscriptions",
            component: () =>
              import(
                "@/components/app/global/contractProducts/productsTable.vue"
              )
          }
        ]
      },
      {
        path: "otps",
        name: "adminClientOTPs",
        redirect: { name: "adminClientActiveOTPs" },
        component: () =>
          import(
            "@/views/admin/clients/client/contractProducts/otps/index.vue"
          ),
        props: (route: Route) => ({
          clientId: route.params.clientId,
          routes: require("./menu").cProdOTPsTabigation
        }),
        meta: {
          backTo: "_.one_time_purchases",
          title: "_.one_time_purchases"
        },
        children: [
          {
            path: "active",
            name: "adminClientActiveOTPs",
            component: () =>
              import(
                "@/components/app/global/contractProducts/productsTable.vue"
              ),
            props: {
              statuses: [
                ContractStatusCodes.ACTIVE,
                ContractStatusCodes.AWAITING_ACTIVATION,
                ContractStatusCodes.PENDING,
                ContractStatusCodes.SUSPENDED
              ]
            }
          },
          {
            path: "cancelled",
            name: "adminClientCancelledOTPs",
            component: () =>
              import(
                "@/components/app/global/contractProducts/productsTable.vue"
              ),
            props: {
              statuses: [
                ContractStatusCodes.CANCELLED,
                ContractStatusCodes.CLOSED
              ]
            }
          },
          {
            path: "all",
            name: "adminClientAllOTPs",
            component: () =>
              import(
                "@/components/app/global/contractProducts/productsTable.vue"
              )
          }
        ]
      }
    ]
  },
  {
    path: `products/:contractId(${RegexMatch.UUID})/:productId(${RegexMatch.UUID})`,
    component: {
      name: "AdminClientContractProductWrapper",
      props: {
        client: { default: null },
        accountId: { default: null }
      },
      template: "<router-view :key='$route.params.productId.toString()' />"
    },
    children: [
      {
        path: "",
        name: "adminClientCProd",
        redirect: "overview",
        component: () =>
          import(
            "@/views/admin/clients/client/contractProducts/productOverview/index.vue"
          ),
        props: (route: Route) => ({
          clientId: route.params.clientId,
          contractId: route.params.contractId,
          productId: route.params.productId,
          routes: require("./menu").cProdTabigation
        }),
        meta: {
          title: "_.product_and_service"
        },
        children: [
          {
            path: "overview",
            name: "adminClientCProdOverview",
            component: () =>
              import(
                "@/components/app/global/contractProducts/cProdOverview.vue"
              ),
            meta: {
              backTo: "_.overview"
            },
            beforeEnter: (to, from, next) => {
              $bus.$emit("cProdProvider::reloadData");
              next();
            }
          },
          {
            path: "invoices",
            name: "adminClientCProdInvoices",
            component: () =>
              import(
                "@/components/app/global/client/contractProducts/contractProductInvoices.vue"
              ),
            meta: {
              backTo: "_.invoices"
            }
          },
          {
            path: "credit-notes",
            name: "adminClientCProdCreditNotes",
            component: () =>
              import(
                "@/components/app/global/client/contractProducts/contractProductCreditNotes.vue"
              ),
            meta: {
              backTo: "_.credit_notes"
            }
          },
          {
            path: "settings",
            name: "adminClientCProdSettings",
            component: () =>
              import(
                "@/components/app/global/client/contractProducts/contractProductSettings.vue"
              ),
            meta: {
              backTo: "_.settings"
            }
          },
          {
            path: "activity-log",
            name: "adminClientCProdActivities",
            component: () =>
              import(
                "@/views/admin/clients/client/contractProducts/activities/index.vue"
              ),
            meta: {
              backTo: "_.activity_log"
            }
          },
          {
            path: "manage",
            name: "adminClientCProdProvisioning",
            component: () =>
              import(
                "@/views/admin/clients/client/contractProducts/cProdProvisioning/index.vue"
              ),
            meta: {
              backTo: "_.settings"
            }
          },
          {
            path: "delegates",
            name: "adminClientCProdDelegates",
            component: () =>
              import(
                "@/components/app/global/client/contractProducts/contractProductDelegates.vue"
              ),
            meta: {
              backTo: "_.delegates"
            }
          }
        ]
      },
      {
        path: "",
        name: "adminClientCProdQuickLookModal",
        beforeEnter: async (
          to: Route,
          from: Route,
          next: (options: boolean | Location | Location["path"]) => void
        ) => {
          const { clientId, contractId, productId } = to.params;
          if (clientId && contractId && productId) {
            store.dispatch("ui/open/slideModal", {
              config: {
                component: () =>
                  import(
                    "@/components/app/global/contractProducts/cProdQuickLookModal.vue"
                  ),
                canCancel: ["button", "escape", "outside"],
                props: to.params
              }
            });
          }
          store.dispatch("ui/endRouting");
          return next(false);
        }
      }
    ]
  }
];
